import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { VenueTableFilterTypes } from "~pages/Venues/enums/venueTableFilterTypes.ts";
import { useGetVenueTableColumns } from "~pages/Venues/hooks/useGetVenueTableColumns.tsx";
import { useGetVenueTableFilters } from "~pages/Venues/hooks/useGetVenueTableFilters.tsx";
import { useFetchSeasonVenues } from "~pages/Venues/SeasonVenueListPage/hooks/useFetchSeasonVenues.ts";

const DEFAULT_SORTER = { columnKey: "nameLocal", order: "descend" as const };

const SeasonVenueListPage = () => {
  const { sport, customerId, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const paginationConfig = useHandlePagination(20);
  const [searchText, setSearchText] = useState("");
  const [massEditMode, setMassEditMode] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.venues.listPage;
  const { currentFilter, filters, handleFilterChange } = useGetVenueTableFilters();

  const handleSearch = useCallback(
    (value: string) => {
      paginationConfig.resetPagination();
      setSearchText(value);
    },
    [paginationConfig],
  );

  const handleFiltersChange = useCallback(
    (updatedFilter: Record<string, unknown>) => {
      paginationConfig.resetPagination();
      handleFilterChange(updatedFilter);
    },
    [handleFilterChange, paginationConfig],
  );

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      status: currentFilter[VenueTableFilterTypes.Status]?.value,
      countryCode: currentFilter[VenueTableFilterTypes.CountryCode]?.value,
      nameLocalContains: searchText,
    }),
    [currentFilter, paginationConfig.offset, paginationConfig.pageSize, searchText, sortBy],
  );

  const { data, refetch, isPending } = useFetchSeasonVenues({
    customParams: listParams,
    enabled: sortBy !== null,
  });

  const columns = useGetVenueTableColumns({ massEditMode });

  const listUrl = `/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/venues`;
  const getManageUrl = (venueId: string) => `/${customerId}/admin/${sport}/${organizationId}/venues/manage/${venueId}`;

  const onMassEditModeChangedHandler = useCallback((checked: boolean) => {
    setMassEditMode(checked);
  }, []);

  return (
    <DataTable
      handleRefresh={async () => {
        await refetch();
      }}
      title={formatMessage({ id: "venues" })}
      columns={columns}
      rowActions={{
        base: {
          manage: {
            onClick: (row) => navigate(getManageUrl(row.venueId), { state: { from: listUrl } }),
          },
        },
      }}
      rowNavigation={{
        getNavigateUrl: (row) => getManageUrl(row.venueId),
      }}
      rowKey="venueId"
      rowNameKey="nameLocal"
      dataSource={data?.data || []}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.seasonVenues`}
      isDataLoading={isPending}
      filtersConfig={{
        currentFilter,
        filters,
        handleFilterChange: handleFiltersChange,
      }}
      searchConfig={{
        handleSearch,
        searchText,
      }}
      onMassEditModeChangedHandler={onMassEditModeChangedHandler}
      massEditMode={massEditMode}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.seasonVenues`,
        defaultSorter: DEFAULT_SORTER,
        onSortChange: setSortBy,
      }}
      permissionNames={[PermissionNames.VENUE]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default SeasonVenueListPage;
